import React, { useState, useEffect } from 'react';
import "./Certificate.css"
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MAIN_URL } from '../../constants';
import { Award } from 'lucide-react';
import { Button, Card, EmptyState, PageHeader, Pagination, TableSkeleton } from '../../Components/ui';
import './CertificateHolders.css';

// Faqat ko'rsatish uchun: "24/01/2026" → "24.01.2026" (sana groupCertificatesByUser'da hisoblanadi).
const formatDisplayDate = (date) => (typeof date === 'string' ? date.replace(/\//g, '.') : date);

const getInitials = (name = '') =>
    name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join('');

const CertificateHolders = () => {
    const navigate = useNavigate();
    const [certificates, setCertificates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const itemsPerPage = 15;

    useEffect(() => {
        fetchCertificates();
    }, [currentPage]);

    const fetchCertificates = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('admin_token');

            const skip = (currentPage - 1) * itemsPerPage;

            const response = await axios.get(`${MAIN_URL}/certificates/`, {
                params: {
                    skip: skip,
                    limit: itemsPerPage
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            setTotalItems(response.data.total);

            // Ma'lumotlarni user bo'yicha guruhlash
            const groupedData = groupCertificatesByUser(response.data.result);
            setCertificates(groupedData);
            setError(null);
        } catch (err) {
            console.error('Xatolik:', err);
            if (err.response?.status === 401) {
                setError('Avtorizatsiya xatosi. Iltimos, qayta kiring.');
            } else {
                setError(err.response?.data?.detail || 'Ma\'lumotlarni yuklashda xatolik');
            }
        } finally {
            setLoading(false);
        }
    };

    // Certificatelarni user bo'yicha guruhlash
    const groupCertificatesByUser = (certificates) => {
        const grouped = {};

        certificates.forEach(cert => {
            const userId = cert.user.id;

            if (!grouped[userId]) {
                grouped[userId] = {
                    id: userId,
                    name: `${cert.user.first_name} ${cert.user.last_name}`,
                    certificates: []
                };
            }

            grouped[userId].certificates.push({
                id: cert.course.id,
                name: cert.course.title,
                date: new Date(cert.created_at).toLocaleDateString('uz-UZ'),
                certificateId: cert.id
            });
        });

        return Object.values(grouped);
    };

    const handleCertificateClick = (userId, courseId, certificate) => {
        if (courseId) {
            navigate(`/certificate/${userId}/${courseId}`, {
                state: { certificate }
            });
        }
    };


    // Umumiy sahifalar soni
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    // Sahifa o'zgarganda
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const header = (
        <PageHeader
            title="Sertifikat egalari"
            subtitle={loading || error ? undefined : `Jami: ${totalItems} ta sertifikat`}
        />
    );

    if (loading) {
        return (
            <div className="ui-holders">
                {header}
                <TableSkeleton rows={8} cols={4} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="ui-holders">
                {header}
                <Card padding="none">
                    <EmptyState
                        title="Ma'lumotlarni yuklab bo'lmadi"
                        description={`Xatolik: ${error}`}
                        action={
                            <Button variant="secondary" size="sm" onClick={fetchCertificates}>
                                Qayta urinish
                            </Button>
                        }
                    />
                </Card>
            </div>
        );
    }

    return (
        <div className="ui-holders">
            {header}

            {certificates.length === 0 ? (
                <Card padding="none">
                    <EmptyState icon={<Award />} title="Sertifikatlar topilmadi" />
                </Card>
            ) : (
                <div className="ui-table-wrap">
                    <table className="ui-table ui-holders__table">
                        <thead>
                            <tr>
                                <th className="ui-table-cell--numeric">№</th>
                                <th>Student</th>
                                <th>Sertifikatlar</th>
                                <th>Sana</th>
                            </tr>
                        </thead>
                        <tbody>
                            {certificates.map((holder, index) => (
                                holder.certificates.length > 0 && (
                                    <tr key={holder.id}>
                                        <td className="ui-table-cell--numeric ui-holders__index">
                                            {(currentPage - 1) * itemsPerPage + index + 1}
                                        </td>
                                        <td>
                                            <div className="ui-holders__person">
                                                <span className="ui-holders__avatar" aria-hidden="true">
                                                    {getInitials(holder.name)}
                                                </span>
                                                <span className="ui-holders__name">{holder.name}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="ui-holders__certs">
                                                {holder.certificates.map((cert, certIndex) => (
                                                    <button
                                                        key={certIndex}
                                                        type="button"
                                                        className="ui-holders__cert"
                                                        title={`${cert.name} · ${formatDisplayDate(cert.date)}`}
                                                        onClick={() => handleCertificateClick(holder.id, cert.certificateId, holder)}
                                                    >
                                                        <Award aria-hidden="true" />
                                                        <span className="ui-holders__cert-name">{cert.name}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="ui-holders__date">
                                            {formatDisplayDate(holder.certificates[0].date)}
                                        </td>
                                    </tr>
                                )
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="ui-holders__footer">
                    <span className="ui-holders__muted">
                        Sahifa {currentPage} / {totalPages}
                    </span>
                    <Pagination
                        page={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}
        </div>
    );
};

export default CertificateHolders;